import { useState, useEffect, useCallback, useRef } from 'react';
import { useServices } from '@/di/precon-supporter';
import type { LearnerProfile } from '@/domain/precon-supporter';

/**
 * タブ間同期用のBroadcastChannelの名前
 * ニックネーム保存時に他のタブへ通知するために使用
 */
const LEARNER_PROFILE_CHANNEL_NAME = 'precon-learner-profile-sync';

/**
 * BroadcastChannelで送受信するメッセージの型
 */
type ProfileSyncMessage = {
  type: 'NICKNAME_SAVED';
  nickname: string;
};

/**
 * 受講者プロフィール管理フック
 *
 * 1ブラウザ = 1受講者の前提で、プロフィール情報を管理する。
 */
export const useLearnerProfile = () => {
  const [profile, setProfile] = useState<LearnerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const services = useServices();
  const {
    getLearnerProfileUseCase,
    setWantsAdvancedCourseUseCase,
    setNicknameUseCase,
  } = services;

  // BroadcastChannelの参照を保持
  const channelRef = useRef<BroadcastChannel | null>(null);

  // プロフィール取得
  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const fetchedProfile = await getLearnerProfileUseCase.execute();
      setProfile(fetchedProfile);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('プロフィールの取得に失敗しました')
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // 初回マウント時にプロフィールを取得
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // BroadcastChannelによるタブ間同期
  useEffect(() => {
    // BroadcastChannel APIがサポートされていない環境では何もしない
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    const channel = new BroadcastChannel(LEARNER_PROFILE_CHANNEL_NAME);
    channelRef.current = channel;

    // 他のタブからのメッセージを受信
    const handleMessage = (event: MessageEvent<ProfileSyncMessage>) => {
      if (event.data.type === 'NICKNAME_SAVED') {
        // 他のタブでニックネームが保存されたので、プロフィールを再取得
        fetchProfile();
      }
    };

    channel.addEventListener('message', handleMessage);

    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
      channelRef.current = null;
    };
  }, [fetchProfile]);

  // タブがフォーカスされた時にプロフィールを再取得（フォールバック）
  useEffect(() => {
    const handleFocus = () => {
      fetchProfile();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchProfile]);

  // アドバンスト編受講希望を設定
  const setWantsAdvancedCourse = useCallback(
    async (wants: boolean) => {
      try {
        setLoading(true);
        setError(null);

        await setWantsAdvancedCourseUseCase.execute(wants);

        // プロフィールを再取得
        await fetchProfile();
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('アドバンスト編受講希望の設定に失敗しました')
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchProfile]
  );

  // ニックネームを設定（1回のみ設定可能）
  const setNickname = useCallback(
    async (nickname: string) => {
      try {
        setLoading(true);
        setError(null);

        await setNicknameUseCase.execute(nickname);

        // プロフィールを再取得
        await fetchProfile();

        // 他のタブへニックネーム保存を通知
        if (channelRef.current) {
          const message: ProfileSyncMessage = {
            type: 'NICKNAME_SAVED',
            nickname,
          };
          channelRef.current.postMessage(message);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('ニックネームの設定に失敗しました')
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchProfile]
  );

  return {
    profile,
    loading,
    error,
    wantsAdvancedCourse: profile?.getWantsAdvancedCourse() ?? false,
    nickname: profile?.getNickname() ?? null,
    setWantsAdvancedCourse,
    setNickname,
    refetch: fetchProfile,
  };
};
